import { useEffect } from 'react';
import { useEtsyContext } from '@/contexts/EtsyContext';
import { supabase } from '@/integrations/supabase/client';
import { getCachedUserData } from '@/utils/userCache';
import { calculateOrderMetricsForProducts } from '@/utils/orderMetricsCalculator';
import { DEMO_ORDERS, DEMO_PRODUCTS } from '@/utils/demoData';
import { 
  getCachedOrders, 
  setCachedOrders, 
  getCachedProducts, 
  setCachedProducts,
  shouldFetchOrders,
  shouldFetchProducts,
  clearCache
} from '@/utils/etsyCache';

// Global flags to prevent multiple simultaneous calls
let isCheckingConnection = false;
let isFetchingOrders = false;
let isFetchingProducts = false;

export const useEtsyStore = () => {
  const { dispatch, hasActiveSubscription, selectedStoreIds, ...state } = useEtsyContext();

  const fetchUserStores = async (): Promise<string[]> => {
    if (!hasActiveSubscription) return [];

    try {
      const user = await getCachedUserData();
      if (!user) return [];

      const { data: shops } = await supabase
        .from('etsy_shops')
        .select('id, shop_name')
        .eq('user_id', user.id);

      if (shops && shops.length > 0) {
        const storeIds = shops.map(shop => shop.id);
        console.log(`Found ${storeIds.length} stores for user:`, shops.map(s => s.shop_name));
        return storeIds;
      }

      return [];
    } catch (error) {
      console.error('Error fetching user stores:', error);
      return [];
    }
  };

  const checkConnection = async (forceRefresh = false): Promise<boolean> => {
    if (!hasActiveSubscription) {
      // For demo mode, simulate a connection
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
      return true;
    }

    // Check if we recently checked and don't force refresh
    if (!forceRefresh && state.lastConnectionCheck) {
      const timeSinceCheck = Date.now() - state.lastConnectionCheck.getTime();
      const fiveMinutes = 5 * 60 * 1000;
      if (timeSinceCheck < fiveMinutes) {
        console.log('Using cached connection status:', state.isConnected);
        return state.isConnected;
      }
    }

    // Prevent multiple simultaneous connection checks
    if (isCheckingConnection) {
      return state.isConnected;
    }

    isCheckingConnection = true;
    dispatch({ type: 'SET_CHECKING_CONNECTION', payload: true });

    try {
      const user = await getCachedUserData();
      if (!user) {
        dispatch({ type: 'SET_STATE', payload: { 
          isConnected: false, 
          isCheckingConnection: false, 
          lastConnectionCheck: new Date() 
        }});
        return false;
      }

      const { data: shops } = await supabase
        .from('etsy_shops')
        .select('shop_name')
        .eq('user_id', user.id);

      const isConnected = shops && shops.length > 0;
      
      console.log('Fetched and cached Etsy connection status:', isConnected);
      dispatch({ type: 'SET_STATE', payload: { 
        isConnected, 
        isCheckingConnection: false, 
        lastConnectionCheck: new Date() 
      }});
      
      return isConnected;
    } catch (error) {
      console.error('Error checking Etsy connection:', error);
      dispatch({ type: 'SET_STATE', payload: { 
        isConnected: false, 
        isCheckingConnection: false, 
        lastConnectionCheck: new Date() 
      }});
      return false;
    } finally {
      isCheckingConnection = false;
    }
  };

  const fetchOrders = async (forceRefresh = false, offset = 0): Promise<void> => {
    if (!hasActiveSubscription) {
      dispatch({ type: 'SET_ORDERS', payload: DEMO_ORDERS });
      return;
    }
    if (isFetchingOrders) return;

    // Only two conditions to call the API for orders:
    // 1. No timestamp OR last fetched date is a different day (shouldFetchOrders)
    // 2. forceRefresh is true (e.g., Sync Data button)
    // Only test cache on first (offset==0) page fetch
    if (offset === 0 && !forceRefresh && !shouldFetchOrders()) {
      const cachedOrders = getCachedOrders();
      if (cachedOrders.length > 0) {
        dispatch({ type: 'SET_ORDERS', payload: cachedOrders });
        return;
      }
    }

    isFetchingOrders = true;
    if (offset === 0) {
      dispatch({ type: 'SET_FETCHING_ORDERS', payload: true });
    } else {
      dispatch({ type: 'SET_LOADING_MORE_ORDERS', payload: true });
    }

    try {
      // Fetch orders from all selected stores
      const allOrders = [];
      for (const storeId of selectedStoreIds) {
        const { data, error } = await supabase.functions.invoke('fetch-etsy-receipts', {
          body: { 
            offset,
            storeId
          }
        });
        if (error) throw error;
        const orders = data.orders || [];
        allOrders.push(...orders);
      }

      if (offset === 0) {
        dispatch({ type: 'SET_ORDERS', payload: allOrders });
        setCachedOrders(allOrders);
      } else {
        dispatch({ type: 'APPEND_ORDERS', payload: allOrders });
        const combinedOrders = [...state.orders, ...allOrders];
        setCachedOrders(combinedOrders);
      }
      dispatch({ 
        type: 'SET_ORDERS_PAGINATION', 
        payload: { 
          offset: offset + allOrders.length, 
          hasMore: allOrders.length > 0 // Simplified hasMore logic
        } 
      });
      dispatch({ type: 'SET_STATE', payload: { 
        isFetchingOrders: false,
        isLoadingMoreOrders: false,
        lastOrdersFetch: new Date() 
      }});
    } catch (error) {
      dispatch({ type: 'SET_STATE', payload: { 
        isFetchingOrders: false,
        isLoadingMoreOrders: false
      }});
    } finally {
      isFetchingOrders = false;
    }
  };

  const fetchProducts = async (forceRefresh = false, offset = 0): Promise<void> => {
    if (!hasActiveSubscription) {
      // Load demo products for free plan users
      console.log('Loading demo products for free plan user');
      dispatch({ type: 'SET_PRODUCTS', payload: DEMO_PRODUCTS });
      return;
    }

    if (isFetchingProducts) {
      return;
    }

    // Check if we should use cached data (only for first page)
    if (offset === 0 && !forceRefresh && !shouldFetchProducts()) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts.length > 0) {
        console.log('Using cached products');
        dispatch({ type: 'SET_PRODUCTS', payload: cachedProducts });
        return;
      }
    }

    isFetchingProducts = true;
    if (offset === 0) {
      dispatch({ type: 'SET_FETCHING_PRODUCTS', payload: true });
    } else {
      dispatch({ type: 'SET_LOADING_MORE_PRODUCTS', payload: true });
    }

    try {
      console.log(`Fetching products from Etsy API with offset ${offset}...`);
      
      // Fetch products from all selected stores
      const allProducts = [];
      for (const storeId of selectedStoreIds) {
        const { data, error } = await supabase.functions.invoke('fetch-etsy-products', {
          body: { 
            offset,
            storeId
          }
        });
        
        if (error) throw error;

        const formattedProducts = data.products?.map((product: any) => ({
          id: `${product.listing_id}`,
          title: product.title,
          description: product.description,
          price: product.price,
          originalPrice: product.original_price || product.price,
          currencyCode: product.currency_code,
          quantity: product.quantity,
          salesCount: 0,
          refundCount: 0,
          cancelledCount: 0,
          state: product.state,
          url: product.url,
          mainImageUrl: product.main_image_url,
          imageUrls: product.image_urls || [product.main_image_url].filter(Boolean), // Use all images or fallback to main image
          shippingProfiles: product.shipping_profiles || [], // Add shipping information with destination countries
          listingId: product.listing_id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          views: product.views || 0,
        })) || [];

        allProducts.push(...formattedProducts);
      }

      // Calculate order metrics from cached orders using the utility
      const orders = state.orders.length > 0 ? state.orders : getCachedOrders();
      const productsWithMetrics = calculateOrderMetricsForProducts(allProducts, orders);

      if (offset === 0) {
        // First page - replace products
        dispatch({ type: 'SET_PRODUCTS', payload: productsWithMetrics });
        setCachedProducts(productsWithMetrics);
      } else {
        // Additional pages - append products and recalculate metrics for all
        const combinedProducts = [...state.products, ...productsWithMetrics];
        const allProductsWithMetrics = calculateOrderMetricsForProducts(combinedProducts, orders);
        dispatch({ type: 'SET_PRODUCTS', payload: allProductsWithMetrics });
        setCachedProducts(allProductsWithMetrics);
      }

      // Update pagination state
      dispatch({ 
        type: 'SET_PRODUCTS_PAGINATION', 
        payload: { 
          offset: offset + allProducts.length, 
          hasMore: allProducts.length > 0 // Simplified hasMore logic
        } 
      });

      dispatch({ type: 'SET_STATE', payload: { 
        isFetchingProducts: false,
        isLoadingMoreProducts: false,
        lastProductsFetch: new Date() 
      }});
      
      console.log(`Fetched ${productsWithMetrics.length} products from Etsy (offset: ${offset}) with all images, shipping destination countries`);

    } catch (error) {
      console.error('Error fetching Etsy products:', error);
      dispatch({ type: 'SET_STATE', payload: { 
        isFetchingProducts: false,
        isLoadingMoreProducts: false
      }});
    } finally {
      isFetchingProducts = false;
    }
  };

  const loadMoreOrders = async (): Promise<void> => {
    if (!state.hasMoreOrders || state.isLoadingMoreOrders) return;
    await fetchOrders(false, state.ordersOffset);
  };

  const loadMoreProducts = async (): Promise<void> => {
    if (!state.hasMoreProducts || state.isLoadingMoreProducts) return;
    await fetchProducts(false, state.productsOffset);
  };

  const initializeData = async (): Promise<void> => {
    if (!hasActiveSubscription) {
      // Load demo data immediately for free plan users
      console.log('Initializing demo data for free plan user');
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
      dispatch({ type: 'SET_ORDERS', payload: DEMO_ORDERS });
      dispatch({ type: 'SET_PRODUCTS', payload: DEMO_PRODUCTS });
      return;
    }

    // Check connection first
    const isConnected = await checkConnection();
    
    if (isConnected) {
      // If no stores are selected, automatically fetch and select user's stores
      let storeIds = selectedStoreIds;
      if (storeIds.length === 0) {
        console.log('No stores selected, fetching user stores...');
        const userStores = await fetchUserStores();
        if (userStores.length > 0) {
          // Automatically select all user stores
          dispatch({ type: 'SET_SELECTED_STORES', payload: userStores });
          storeIds = userStores;
          console.log('Automatically selected user stores:', storeIds);
        }
      }

      if (storeIds.length > 0) {
        // Load cached data immediately
        const cachedOrders = getCachedOrders();
        const cachedProducts = getCachedProducts();
        
        if (cachedOrders.length > 0) {
          dispatch({ type: 'SET_ORDERS', payload: cachedOrders });
        }
        
        if (cachedProducts.length > 0) {
          dispatch({ type: 'SET_PRODUCTS', payload: cachedProducts });
        }
        
        // Fetch fresh data if needed
        await Promise.all([
          fetchOrders(),
          fetchProducts()
        ]);
      }
    }
  };

  // Initialize data when conditions are met
  useEffect(() => {
    initializeData();
  }, [hasActiveSubscription]);

  // Re-initialize when selected stores change (but not on initial mount)
  useEffect(() => {
    if (selectedStoreIds.length > 0) {
      console.log('Selected stores changed:', selectedStoreIds);
      // Fetch fresh data for the new store selection
      Promise.all([
        fetchOrders(),
        fetchProducts()
      ]);
    }
  }, [selectedStoreIds]);

  return {
    // State
    userId: null, // Will be handled by AuthContext
    hasActiveSubscription,
    isSubscriptionLoading: false, // Will be handled by subscription hook
    isConnected: state.isConnected,
    isCheckingConnection: state.isCheckingConnection,
    orders: state.orders,
    products: state.products,
    isFetchingOrders: state.isFetchingOrders,
    isFetchingProducts: state.isFetchingProducts,
    hasMoreOrders: state.hasMoreOrders,
    hasMoreProducts: state.hasMoreProducts,
    isLoadingMoreOrders: state.isLoadingMoreOrders,
    isLoadingMoreProducts: state.isLoadingMoreProducts,
    selectedStoreIds,
    
    // Actions
    checkConnection,
    fetchOrders,
    fetchProducts,
    loadMoreOrders,
    loadMoreProducts,
    initializeData,
  };
};
